import { useState, useEffect, useCallback, useRef } from 'react';
import toast from 'react-hot-toast';
import { RefreshCw, Plus, Edit, Check, X, Trash2 } from 'lucide-react';
import PropTypes from 'prop-types';
import { chatsApi } from 'api';
import GenericModal from '../modals/GenericModal.jsx';
const ChatSessionsSection = ({ onNavigate }) => {
  const navigate = onNavigate;

  const [sessions, setSessions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [activeSessionId, setActiveSessionId] = useState(null);
  const [editingSessionId, setEditingSessionId] = useState(null);
  const [deletingSessionId, setDeletingSessionId] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [newSessionTitle, setNewSessionTitle] = useState('');
  const editInputRef = useRef(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [modalVisible, setModalVisible] = useState(false);
  
  const setActiveSession = useCallback(async (sessionId) => {
    setActiveSessionId(sessionId);
    if (window.location.pathname === '/campaign-creation') {
      navigate(`/campaign-creation?chat=${sessionId}`);
    }
  }, [navigate]);

  // detech when url changes, and use that sessionId as activeSessionId
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const chatParam = urlParams.get('chat');
    if (chatParam) {
      setActiveSessionId(parseInt(chatParam));
    }
  }, [navigate]);

  const DEFAULT_PAGE_SIZE = 10;
  const loadSessions = useCallback(async (newPage = 1) => {
    setIsLoading(true);
    try {
      const sessRes = await chatsApi.getChatSessions({ per_page: DEFAULT_PAGE_SIZE, page: newPage });
      const lastSessionId = sessRes.data[0]?.id;
      const hasChatParam = window.location.search.includes('chat=');
      if (!activeSessionId && !hasChatParam) {
        setActiveSessionId(lastSessionId);
      }

      const newSessions = sessRes.data.sort((a, b) =>
        new Date(b.updated_at) - new Date(a.updated_at)
      );

      if (newPage === 1) {
        setSessions(newSessions);
      } else {
        setSessions(prev => [...prev, ...newSessions]);
      }

      setHasMore(newSessions.length === DEFAULT_PAGE_SIZE);

      if (!newSessions.length && newPage === 1) {
        const newSession = await chatsApi.createChatSession();
        setSessions([newSession.data]);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  }, [activeSessionId]);

  const handleLoadMore = useCallback(() => {
    const nextPage = page + 1;
    setPage(nextPage);
    loadSessions(nextPage);
  }, [page, loadSessions]);

  const handleCreate = useCallback(async () => {
    try {
      const resp = await chatsApi.createChatSession();
      const newSess = resp.data;
      setSessions(prev => [newSess, ...prev]);
      setActiveSession(newSess.id);
      navigate(`/campaign-creation?chat=${newSess.id}`);
    } catch (e) {
      const errorMessage = e?.response?.data?.error || e?.message || 'Error genérico';
      toast.error(`No se pudo crear la conversación: ${errorMessage}`);
    }
  }, [navigate, setActiveSession]);

  const startEdit = useCallback(session => {
    setEditingSessionId(session.id);
    setNewSessionTitle(
      session.title || `Chat ${new Date(session.created_at).toLocaleString()}`
    );
  }, []);

  const saveTitle = useCallback(async id => {
    if (!newSessionTitle.trim()) {
      toast.error('El título no puede estar vacío');
      return;
    }
    try {
      await chatsApi.updateChatSession(id, { title: newSessionTitle.trim() });
      setSessions(sessions =>
        sessions.map(x =>
          x.id === id ? { ...x, title: newSessionTitle.trim() } : x
        )
      );
      setEditingSessionId(null);
      toast.success('El título se ha actualizado correctamente');
    } catch {
      toast.error('No se pudo actualizar el título');
    }
  }, [newSessionTitle]);

  const cancelEdit = () => {
    setEditingSessionId(null);
    setNewSessionTitle('');
  };

  const handleArchiveClick = useCallback(async id => {
    if (sessions.length === 1) {
      console.error('No se puede eliminar el único chat');
      return;
    }

    setModalVisible(true);
    setDeletingSessionId(id);
  }, [sessions.length]);

  const handleArchive = useCallback(async id => {
    try {
      setIsDeleting(true);
      await chatsApi.archiveChatSession(id);
      setSessions(prev => prev.filter(s => s.id !== id));
      toast.success('El chat ha sido eliminado correctamente');

      if (id === activeSessionId && sessions.length > 1) {
        const nextId = sessions.find(s => s.id !== id)?.id;
        setActiveSession(nextId);
      }
    } catch {
      toast.error('No se pudo eliminar el chat');
    } finally {
      setDeletingSessionId(null);
      setIsDeleting(false);
      setModalVisible(false);
    }
  }, [activeSessionId, sessions, setActiveSession]);

  useEffect(() => {
    loadSessions();
  }, [loadSessions]);

  useEffect(() => {
    if (editingSessionId && editInputRef.current) {
      editInputRef.current.focus();
    }
  }, [editingSessionId]);

  useEffect(() => {
    const handler = e => {
      if (e.detail?.sessionData) {
        setSessions(prev => [e.detail.sessionData, ...prev]);
      } else {
        loadSessions();
      }
    };
    window.addEventListener('newChatSessionCreated', handler);
    return () => window.removeEventListener('newChatSessionCreated', handler);
  }, [loadSessions]);

  const isActive = id => id === activeSessionId;

  // if the user is in /campaign-creation, with no chat param, set the first chat as active
  useEffect(() => {
    const pathname = window.location.pathname;
    const hasChatParam = window.location.search.includes('chat=');
    const hasSessions = sessions.length > 0;
    if (pathname === '/campaign-creation' && !hasChatParam && hasSessions) {
      setActiveSession(sessions[0].id);
    }
  }, [sessions, setActiveSession]);

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between px-3 py-2 border-b">
        <span className="text-sm font-medium text-gray-600">Chats</span>
        <div className="flex gap-1">
          <button
            onClick={() => loadSessions(1)}
            title="Actualizar chats"
            className="p-1 rounded-lg hover:bg-gray-100"
          >
            <RefreshCw size={14} />
          </button>
          <button
            onClick={handleCreate}
            title="Nuevo chat"
            data-cy="new-chat-button"
            className="p-1 rounded-lg hover:bg-gray-100"
          >
            <Plus size={14} />
          </button>
        </div>
      </div>

      <GenericModal
        isOpen={modalVisible}
        toggle={() => setModalVisible(!modalVisible)}
        title="Eliminar chat"
        onSubmit={() => handleArchive(deletingSessionId)}
        onClose={() => setModalVisible(!modalVisible)}
        isSubmitting={isDeleting}
        submitText={isDeleting ? "Eliminando..." : "Eliminar"}
        cancelText="Cancelar"
        closable={true}
        showLogo={false}
        showSubmitButton={true}
        showCloseButton={true}
      >
        <p className="text-gray-500">¿Estás seguro de que deseas eliminar este chat?</p>
      </GenericModal>

      <div className="flex-1 overflow-y-auto my-2 py-2">
        {isLoading && page === 1 ? (
          <div className="flex justify-center py-4">
            <RefreshCw className="animate-spin" size={18} />
          </div>
        ) : sessions.length ? (
          <>
            <div className="space-y-1 p-2">
              {sessions.map(session => (
                <div
                  key={session.id}
                  className={`
                    group flex ${editingSessionId === session.id ? 'flex-col' : 'items-center'} gap-2
                    px-3 py-4 rounded-lg transition-colors cursor-pointer
                    ${isActive(session.id) ? 'bg-blue-50 border border-blue-200' : 'hover:bg-gray-100'}
                  `}
                >
                  {editingSessionId === session.id ? (
                    <>
                      <input
                        ref={editInputRef}
                        value={newSessionTitle}
                        onChange={e => setNewSessionTitle(e.target.value)}
                        onKeyDown={e => {
                          if (e.key === 'Enter') saveTitle(session.id);
                          if (e.key === 'Escape') cancelEdit();
                        }}
                        className="w-full px-2 py-1 border rounded focus:ring-2"
                      />
                      <div className="flex justify-end gap-2 mt-2">
                        <button onClick={() => saveTitle(session.id)}>
                          <Check size={16} />
                        </button>
                        <button onClick={cancelEdit}>
                          <X size={16} />
                        </button>
                      </div>
                    </>
                  ) : (
                    <>
                      <div
                        className="flex-1 min-w-0"
                        onClick={async () => {
                          setActiveSession(session.id);
                          navigate(`/campaign-creation?chat=${session.id}`);
                        }}
                      >
                        <div className="truncate font-medium">{session.title}</div>
                        {session.last_message && (
                          <div className="truncate text-xs text-gray-500">
                            {session.last_message.slice(0, 60)}…
                          </div>
                        )}
                      </div>
                      <button onClick={() => startEdit(session)}>
                        <Edit size={16} />
                      </button>
                      <button disabled={sessions.length === 1} onClick={() => handleArchiveClick(session.id)}>
                        <Trash2 size={16} />
                      </button>
                    </>
                  )}
                </div>
              ))}
            </div>
            {hasMore && (
              <div className="p-2">
                <button
                  onClick={handleLoadMore}
                  disabled={isLoading}
                  className="w-full py-2 text-sm text-blue-600 hover:text-blue-700 disabled:opacity-50"
                >
                  {isLoading ? 'Cargando...' : 'Cargar más'}
                </button>
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-6">
            <p className="text-gray-500">No hay conversaciones</p>
            <button onClick={handleCreate} className="mt-2 text-blue-600">
              Crear nueva conversación
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

ChatSessionsSection.propTypes = {
  onNavigate: PropTypes.func.isRequired,
};

export default ChatSessionsSection;